import React, { useEffect, useState } from "react";
import { CloudOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getSnapshotMeta } from "@/lib/offline-cache";
import { getSnapshot } from "@/lib/offline-db";

/**
 * Page-level empty state shown when the user is offline AND the route they're
 * on has no cached data backing it (Maintenance, Fleet, Staff, Live Tracking,
 * Weather, etc.). Lives behind DashboardLayout's offline guard.
 *
 * Tells the user three things:
 *   1. This specific section needs connection.
 *   2. What they CAN do offline (calendar / trip details / expenses).
 *   3. When their cached data was refreshed (so they trust the snapshot).
 */
export default function OfflineUnavailable() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [snapshotAt, setSnapshotAt] = useState(null);
  const [available, setAvailable] = useState({
    trips: false,
    chats: false,
    airports: false,
    loaded: false,
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [meta, snap] = await Promise.all([getSnapshotMeta(), getSnapshot()]);
        if (!alive) return;
        setSnapshotAt(meta?.last_snapshot_at || null);
        const tripsCount =
          (snap?.calendar?.items || []).length + Object.keys(snap?.trip_details || {}).length;
        const chatsCount = Object.keys(snap?.trip_chats || {}).length;
        const airportsCount =
          (snap?.cbp_airports?.count || 0) +
          (snap?.aip_mx_airports?.count || 0) +
          (snap?.faa_nasr_airports?.count || 0);
        setAvailable({
          trips: tripsCount > 0,
          chats: chatsCount > 0,
          airports: airportsCount > 0,
          loaded: true,
        });
      } catch {
        if (alive) setAvailable({ trips: false, chats: false, airports: false, loaded: true });
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const ageLabel = snapshotAt ? formatAge(snapshotAt, t) : null;
  // Crear gastos + editar tramos siempre funcionan offline (outbox), incluso
  // si nunca hubo snapshot — se encolan localmente y se sincronizan al
  // recuperar la red. El resto depende del prefetch (12h pre-vuelo, sólo crew).
  const hasAnyRead = available.trips || available.chats || available.airports;

  return (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center max-w-md mx-auto">
      <div className="w-16 h-16 rounded-2xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center mb-5">
        <CloudOff className="w-8 h-8 text-amber-300" />
      </div>
      <h2 className="text-lg font-semibold text-white mb-2">{t("offline.unavailableTitle")}</h2>
      <p className="text-sm text-slate-400 mb-6 leading-relaxed">{t("offline.unavailableBody")}</p>

      <div className="w-full text-left bg-slate-800/40 border border-slate-700/60 rounded-xl px-4 py-3 mb-4">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">
          {t("offline.canDoTitle")}
        </p>
        <ul className="text-sm text-slate-200 space-y-1">
          {available.trips && <li>• {t("offline.canDoCalendar")}</li>}
          {available.chats && <li>• {t("offline.canDoChat")}</li>}
          {available.airports && <li>• {t("offline.canDoAirports")}</li>}
          <li>• {t("offline.canDoExpenses")}</li>
          <li>• {t("offline.canDoLegEdits")}</li>
        </ul>
        {available.loaded && !hasAnyRead && (
          <p className="text-[11px] text-amber-300/80 mt-3">{t("offline.noSnapshotWarning")}</p>
        )}
      </div>

      {ageLabel && (
        <p className="text-[11px] text-slate-400 mb-5">
          {t("offline.snapshotAge", { age: ageLabel })}
        </p>
      )}

      <button
        onClick={() => navigate("/calendar")}
        className="px-4 py-2 rounded-xl bg-altiv-navy hover:bg-altiv-navy-hover text-white text-sm font-medium transition-colors"
      >
        {t("offline.goToCalendar")}
      </button>
    </div>
  );
}

function formatAge(iso, t) {
  try {
    const then = new Date(iso).getTime();
    if (!Number.isFinite(then)) return null;
    const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
    if (mins < 60) {
      return t("offline.ageMinutes", { n: mins });
    }
    const hrs = Math.round(mins / 60);
    if (hrs < 48) {
      return t("offline.ageHours", { n: hrs });
    }
    const days = Math.round(hrs / 24);
    return t("offline.ageDays", { n: days });
  } catch {
    return null;
  }
}
